import React, { useMemo, useState, useEffect, useRef } from 'react';
import { StyleSheet, View } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withSequence,
  withTiming,
} from 'react-native-reanimated';
import { useTheme } from 'styled-components';
import Markdown from '@ronradtke/react-native-markdown-display';
import { merge } from 'lodash';
import styled from 'styled-components/native';
import { Theme } from '../../theme/lightTheme';

// Markdown-it instance with HTML parsing enabled
const markdownItInstance = require('markdown-it')({
  html: true, // Enable HTML tags in markdown
  breaks: true,
  linkify: true,
});
import {
  cleanAndFormatMarkdown,
  markdownRules,
  markdownStyle,
  openMDLink,
} from '../../utils/markdownHelpers';
import { TREATMENT_OPTIONS_HEADERS } from '../../assets/static/chat';
import { wrapUrlsWithMarkdownSchema } from '../../utils/html';
import TextGeneric from '../textGeneric';
import type { StreamingWordTypeWriterProps as Props } from '../../interfaces/components/streamingWordTypeWriter';
import LinkTooltip from '../linkTooltip';

const Text = styled(TextGeneric)`
  user-select: text !important;
  -webkit-user-select: text !important;
  -ms-user-select: text !important;
`;

/**
 * Displays `text` with an elegant typewriter effect,
 * typing out content word by word (30ms per word chunk) with cursor animation.
 * Splits text into word chunks using regex /[^\s]+|\s+/g to handle words and whitespace.
 */
const StreamingWordTypeWriter: React.FC<Props> = ({
  text,
  onAnimationEnd,
  withoutAnimation,
  style,
  typingSpeed = 300, // Default typing speed (words per minute)
  cursorBlinkSpeed = 500, // Default cursor blink speed
  metadata,
  references,
  differentialDiagnosisContext,
}) => {
  const theme = useTheme() as Theme;


  /**
   * Replace [Ref](full_link) placeholders with actual citation links
   * Maps to corresponding references from the citations array
   */
  const replaceReferencePlaceholders = (content: string): string => {
    if (!content || !Array.isArray(references) || references.length === 0) {
      // If no references, just remove the placeholder links
      return content.replace(/\[([^\]]+)\]\((full_link|href)\)/gi, '');
    }

    let refIndex = 0;

    // Replace [text](full_link) or [text](href) with actual URLs from references
    const replaced = content.replace(
      /\[([^\]]+)\]\((full_link|href)\)/gi,
      (match, linkText, placeholder) => {
        if (refIndex >= references.length) {
          // No more references, remove placeholder
          return '';
        }

        const ref = references[refIndex];
        refIndex++;

        // Use full_link as the primary URL
        const targetUrl = ref?.full_link || ref?.genio_url || '';

        if (!targetUrl) {
          return '';
        }

        // Return proper markdown link with actual URL
        return `[${linkText}](${targetUrl})`;
      }
    );

    return replaced;
  };

  const memoizedStyles = useMemo(() => {
    const baseStyles = markdownStyle(theme);
    // Deep merge styles to allow for overrides from props
    return merge(baseStyles, style);
  }, [theme, style]);

  // Helper to get compact label for a URL
  const getCompactLinkLabel = (href: string, metadata: any): string => {
    const meta = metadata?.[href];
    if (meta?.siteName) return meta.siteName;
    if (meta?.title) return meta.title;
    // Fallback to hostname for nicer display
    try {
      const hostname = new URL(href).hostname.replace(/^www\./, '');
      return hostname || href;
    } catch {
      return href; // Fallback to the raw URL
    }
  };

  // Helper to extract domain
  const extractDomain = (url: string): string => {
    try {
      return new URL(url).hostname.replace(/^www\./, '');
    } catch {
      return url;
    }
  };

  // Helper to build a favicon URL when metadata is missing
  const getFallbackFavicon = (url: string): string => {
    const domain = extractDomain(url);
    return `https://www.google.com/s2/favicons?sz=32&domain=${domain}`;
  };

  // Check if the current text contains a diagnosis table
  // This is a direct check on the markdown text, independent of parent context
  const hasDiagnosisTable = useMemo(() => {
    if (!text) return false;
    const lowerText = text.toLowerCase();
    // Check for markdown table with diagnosis-related header
    const hasTableWithDiagnosis =
      lowerText.includes('| diagnosis') ||
      lowerText.includes('|diagnosis') ||
      (lowerText.includes('|') && lowerText.includes('differential diagnosis'));


    return hasTableWithDiagnosis;
  }, [text]);

  const memoRules = useMemo(() => {
    const base = markdownRules(theme);


    // Enhance link rendering using metadata (title + favicon) when available
    base.link = (
      node: any,
      children: any,
      parent: any,
      styles: any,
      onLinkPress: any,
    ) => {
      const href: string = node?.attributes?.href || '';
      const metaForHref = (metadata as any)?.[href];

      // Try to find matching reference for this href
      const normalize = (u?: string) => (u || '').trim().replace(/\/$/, '');
      const hrefNorm = normalize(href);
      const refItem = Array.isArray(references)
        ? references.find((r: any) => {
          const full = normalize(r?.full_link);
          const genio = normalize(r?.genio_url);
          return (
            (!!full && (hrefNorm.includes(full) || full.includes(hrefNorm))) ||
            (!!genio && (hrefNorm.includes(genio) || genio.includes(hrefNorm)))
          );
        })
        : undefined;

      const displayText = refItem?.show_name
        ? (refItem?.website_name || getCompactLinkLabel(href, metadata))
        : getCompactLinkLabel(href, metadata);

      const favicon = metaForHref?.favicon as string | undefined;
      const description = metaForHref?.description as string | undefined;

      return (
        <LinkTooltip
          key={node.key}
          href={href}
          title={displayText}
          description={description}
          favicon={favicon || getFallbackFavicon(href)}
          websiteName={refItem?.website_name}
          genioHref={refItem?.genio_url}
          showName={!!refItem?.show_name}
        >
          {displayText}
        </LinkTooltip>
      );
    };

    // Enhance table row rendering with proper category-specific logic
    // Diagnosis tables: ONLY enhance when isDifferentialDiagnosis = true AND hasDiagnosisTable = true
    // Treatment tables: ONLY enhance when isLatestTreatmentOptions = true AND has treatment table
    const shouldEnhanceTablesForDiagnosis = hasDiagnosisTable && differentialDiagnosisContext?.isDifferentialDiagnosis;
    const shouldEnhanceTablesForTreatment = differentialDiagnosisContext?.isLatestTreatmentOptions;
    const shouldEnhanceTables = shouldEnhanceTablesForDiagnosis || shouldEnhanceTablesForTreatment;

    if (shouldEnhanceTables && differentialDiagnosisContext) {

      // Helper function to extract text from React elements (children)
      // Handles deeply nested React elements like <th><div><span>Text</span></div></th>
      const extractTextFromElement = (element: any): string => {
        if (element === null || element === undefined) {
          return '';
        }
        if (typeof element === 'string') {
          return element;
        }
        if (typeof element === 'number') {
          return String(element);
        }
        if (typeof element === 'boolean') {
          return '';
        }
        if (Array.isArray(element)) {
          return element.map(extractTextFromElement).join(' ');
        }
        if (element && typeof element === 'object') {
          let text = '';

          // Check for React element props.children
          if (element.props) {
            if (element.props.children !== undefined) {
              text += extractTextFromElement(element.props.children);
            }
            // Also check for style.text or other text-like props
            if (typeof element.props.value === 'string') {
              text += ' ' + element.props.value;
            }
          }

          // Check for children property directly
          if (element.children !== undefined) {
            text += ' ' + extractTextFromElement(element.children);
          }

          // Check for content property (markdown-it nodes)
          if (typeof element.content === 'string') {
            text += ' ' + element.content;
          }

          return text.trim();
        }
        return '';
      };

      // Track data row index (excluding header rows)
      let dataRowIndex = 0;

      // Track whether current table has 'Diagnosis' in first column
      let tableHasDiagnosisColumn = false;

      // Store the actual column headers from the table
      let actualColumnHeaders: string[] = [];

      const originalTr = base.tr;
      base.tr = (node: any, children: any) => {
        // Detect if this is a header row by checking the node parent type
        // or by checking if the first child is a th element
        const firstChild = Array.isArray(children) ? children[0] : null;
        const isHeaderRow = node.parent?.type === 'thead' ||
          firstChild?.type === 'th' ||
          (typeof firstChild?.type === 'string' && firstChild.type === 'th');
        const isDataRow = node.parent?.type === 'tbody' || !isHeaderRow;

        // Check if this table should show checkboxes based on context and first column content
        if (isHeaderRow && Array.isArray(children) && children.length > 0) {
          const firstColumnText = extractTextFromElement(children[0]).toLowerCase().trim();

          // CRITICAL FIX: Only show checkboxes for diagnosis tables when in differential_diagnosis category
          const isDiagnosisTable = differentialDiagnosisContext?.isDifferentialDiagnosis &&
            firstColumnText === 'diagnosis';

          // CRITICAL FIX: Only show checkboxes for treatment tables when in latest_treatment_options category
          const isDrugClassTable =
            differentialDiagnosisContext?.isLatestTreatmentOptions &&
            TREATMENT_OPTIONS_HEADERS.some(header =>
              firstColumnText.includes(header.toLowerCase()),
            );

          tableHasDiagnosisColumn = !!(isDiagnosisTable || isDrugClassTable);

          // Extract all column headers from this header row
          if (tableHasDiagnosisColumn) {
            actualColumnHeaders = children.map((child: any) => {
              const headerText = extractTextFromElement(child).trim();
              return headerText || 'Column';
            });
          }

        }

        // Only proceed with checkbox enhancement if this table qualifies for checkboxes
        if (!tableHasDiagnosisColumn) {
          return originalTr(node, children);
        }

        // Get current row index (only count data rows for selection)
        const currentDataRowIndex = isDataRow ? dataRowIndex++ : -1;

        // Check if this row contains diagnosis-related content
        const rowText = extractTextFromElement(children).toLowerCase();


        // Extract row data for differential diagnosis checkbox handling
        const extractRowData = () => {
          const rowData: { [key: string]: string } = {};
          if (Array.isArray(children)) {
            children.forEach((child: any, index: number) => {
              const cellContent = extractTextFromElement(child);

              // Use actual column headers if available, otherwise use fallback names
              const columnName = actualColumnHeaders[index] ||
                (index === 0 ? 'Diagnosis' :
                  index === 1 ? 'Justification' :
                    index === 2 ? 'Tests' :
                      index === 3 ? 'ICD-10' :
                        `Column ${index + 1}`);

              rowData[columnName] = cellContent.trim();
            });
          }


          return rowData;
        };

        // Create checkbox cell for differential diagnosis tables
        let checkboxCell: React.ReactElement | null = null;

        if (isHeaderRow) {
          // Header cell with "Select" text
          checkboxCell = (
            <th key="checkbox-header" style={{
              padding: '10px 8px',
              backgroundColor: theme.secondary + '77',
              border: `1px solid ${theme.text.light}AA`,
              textAlign: 'center' as const,
              width: '50px',
              minWidth: '50px',
              color: theme.text.light,
              fontWeight: 'bold',
            }}>
            </th>
          );
        } else if (isDataRow) {
          // Capture the current checked state at render time
          const isChecked = differentialDiagnosisContext.selectedRows.has(currentDataRowIndex);
          const rowIdx = currentDataRowIndex; // Capture in closure


          // Data cell with checkbox - use a dynamic key to force re-render when selection changes
          const selectionKey = differentialDiagnosisContext.selectedRows.has(rowIdx) ? 'selected' : 'unselected';
          checkboxCell = (
            <td key={`checkbox-cell-${rowIdx}-${selectionKey}`} style={{
              padding: '8px',
              border: `1px solid ${theme.tertiary}`,
              textAlign: 'center' as const,
              width: '50px',
              minWidth: '50px',
            }}>
              <input
                type="checkbox"
                onChange={(e) => {
                  const rowData = extractRowData();
                  differentialDiagnosisContext.toggleRowSelection(rowIdx, rowData);
                }}
                onClick={(e) => {
                  e.stopPropagation();
                }}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: theme.primary,
                }}
              />
            </td>
          );
        }

        // Call the original tr function to get the base row
        const originalRow = originalTr(node, children);

        // If this is a web table row, add the checkbox as the first child
        if (originalRow && React.isValidElement(originalRow) && checkboxCell) {

          const rowProps = originalRow.props as any;
          const existingChildren = rowProps.children || [];
          const childrenArray = Array.isArray(existingChildren) ? existingChildren : [existingChildren];
          const modifiedChildren = [checkboxCell, ...childrenArray];

          return React.cloneElement(originalRow as React.ReactElement<any>, {
            ...rowProps,
            children: modifiedChildren,
          });
        } else {
        }

        return originalRow;
      };
    }

    return base;
  }, [theme, metadata, references, differentialDiagnosisContext, hasDiagnosisTable]);

  const [visibleText, setVisibleText] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isMountedRef = useRef(true);
  const cursorOpacity = useSharedValue(1);
  const scaleValue = useSharedValue(1);

  // Store the full text to compare with incoming text
  const previousTextRef = useRef('');
  const isTypingRef = useRef(false);
  const typedCountRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (withoutAnimation || isTyping) {
      cursorOpacity.value = withTiming(1, { duration: 50 });
      scaleValue.value = withTiming(1.1, { duration: 200 });
      return;
    }

    // When not typing, hide cursor and reset scale
    cursorOpacity.value = withTiming(0, { duration: 200 });
    scaleValue.value = withTiming(1, { duration: 200 });

    const startBlinking = () => {
      if (!isMountedRef.current || isTyping) return;
      cursorOpacity.value = withSequence(
        withTiming(0, { duration: cursorBlinkSpeed }),
        withTiming(1, { duration: cursorBlinkSpeed }),
      );
      timeoutRef.current = setTimeout(startBlinking, cursorBlinkSpeed * 2);
    };
    startBlinking();

    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, [cursorBlinkSpeed, withoutAnimation, isTyping, scaleValue]);

  const cursorStyle = useAnimatedStyle(() => ({
    opacity: cursorOpacity.value,
    transform: [{ scale: scaleValue.value }],
  }));

  useEffect(() => {
    // Replace reference placeholders before cleaning and formatting
    const processedText = replaceReferencePlaceholders(text);
    const fullText = cleanAndFormatMarkdown(processedText);


    if (withoutAnimation || !fullText) {
      setVisibleText(fullText);
      setIsTyping(false);
      isTypingRef.current = false;
      typedCountRef.current = 0;
      scaleValue.value = withTiming(1, { duration: 200 });
      previousTextRef.current = fullText;
      onAnimationEnd?.();
      return;
    }

    // Step 1: Split text into chunks (words + whitespace)
    const newChunks = fullText.match(/[^\s]+|\s+/g) || [];
    // Example: "Hello world" → ["Hello", " ", "world"]

    // Step 2: Split already displayed text the same way
    const oldChunks = previousTextRef.current.match(/[^\s]+|\s+/g) || [];

    // Step 3: Compare to detect if text was appended
    const oldJoined = oldChunks.join('');
    const newJoined = newChunks.join('');

    if (newJoined.startsWith(oldJoined) && newChunks.length > oldChunks.length) {
      // Text was appended: continue from where we left off
      typedCountRef.current = oldChunks.length;
    } else if (newJoined === oldJoined) {
      // No change
      return;
    } else {
      // Text changed completely: reset
      typedCountRef.current = 0;
      setVisibleText('');
      previousTextRef.current = '';
    }

    // Step 4: Start typing animation if not already typing
    if (!isTypingRef.current && newChunks.length > typedCountRef.current) {
      setIsTyping(true);
      isTypingRef.current = true;
      scaleValue.value = withTiming(1.1, { duration: 200 });
    }

    // Step 5: Create interval to add one chunk at a time
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    const typeNextChunk = () => {
      if (!isMountedRef.current) {
        return;
      }

      // Check if text has changed while we were typing
      const processedCurrentText = replaceReferencePlaceholders(text);
      const currentFullText = cleanAndFormatMarkdown(processedCurrentText);
      const currentChunks = currentFullText.match(/[^\s]+|\s+/g) || [];
      const currentJoined = currentChunks.join('');

      // If text has grown, update chunks
      if (currentChunks.length > newChunks.length) {
        const updatedChunks = currentChunks;
        if (typedCountRef.current >= updatedChunks.length) {
          // Finished typing all chunks
          previousTextRef.current = currentFullText;
          setVisibleText(currentFullText);
          setIsTyping(false);
          isTypingRef.current = false;
          scaleValue.value = withTiming(1, { duration: 200 });
          onAnimationEnd?.();
          return;
        }
        // Continue with updated chunks
        typedCountRef.current++;
        const typedSoFar = updatedChunks.slice(0, typedCountRef.current).join('');
        setVisibleText(typedSoFar);
        previousTextRef.current = typedSoFar;
        timeoutRef.current = setTimeout(typeNextChunk, 30); // 30ms per word chunk
        return;
      }

      if (typedCountRef.current >= newChunks.length) {
        // Finished typing all chunks
        previousTextRef.current = newJoined;
        setVisibleText(newJoined);
        setIsTyping(false);
        isTypingRef.current = false;
        scaleValue.value = withTiming(1, { duration: 200 });
        onAnimationEnd?.();
        return;
      }

      // Add next chunk
      typedCountRef.current++;
      const typedSoFar = newChunks.slice(0, typedCountRef.current).join('');
      setVisibleText(typedSoFar);
      previousTextRef.current = typedSoFar;

      // Continue typing next chunk
      timeoutRef.current = setTimeout(typeNextChunk, 30); // 30ms per word chunk
    };

    // Start typing immediately if we have chunks to type
    if (newChunks.length > typedCountRef.current) {
      timeoutRef.current = setTimeout(typeNextChunk, 30);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [text, onAnimationEnd, withoutAnimation, scaleValue, references]);

  // Include differentialDiagnosisContext.selectedRows to force re-render when selection changes
  const selectedRowsArray = differentialDiagnosisContext?.selectedRows
    ? Array.from(differentialDiagnosisContext.selectedRows)
    : [];

  const renderedMarkdown = useMemo(
    () => (
      <Markdown
        markdownit={markdownItInstance}
        rules={memoRules}
        style={memoizedStyles}
        onLinkPress={openMDLink}>
        {wrapUrlsWithMarkdownSchema(visibleText)}
      </Markdown>
    ),
    [visibleText, memoRules, memoizedStyles, selectedRowsArray.length, selectedRowsArray.join(',')],
  );

  if (withoutAnimation) {
    const processedText = replaceReferencePlaceholders(text);
    return (
      <Markdown
        markdownit={markdownItInstance}
        rules={memoRules}
        style={memoizedStyles}
        onLinkPress={openMDLink}>
        {cleanAndFormatMarkdown(wrapUrlsWithMarkdownSchema(processedText))}
      </Markdown>
    );
  }

  return (
    <View style={{ flex: 1 }}>
      {renderedMarkdown}
      {isTyping && (
        <Animated.View style={[styles.cursor, cursorStyle]}>
          <View style={styles.cursorCircle} />
        </Animated.View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  cursor: {
    marginLeft: 4,
    position: 'absolute',
    bottom: 2,
    right: -8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cursorCircle: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#007AFF',
    opacity: 0.8,
  },
});

export default React.memo(StreamingWordTypeWriter);
